import {
  appRoutes,
  companyRoutes,
  contentRoutes,
  formManagerRoutes,
  docsRoutes,
  integrationsRoutes,
  interviewGuideBlueprintRoutes,
  jobRolesRoutes,
  projectsRoutes,
  reportsRoutes,
  resultsRoutes,
  usersRoutes,
} from 'constants/navigation';
import { FC } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import CandidateInfo from 'views/CandidateInfo/CandidateInfo';
import CandidateInfoFields from 'views/CandidateInfo/CandidateInfoFields/CandidateInfoFields';
import CandidateRequirements from 'views/CandidateInfo/CompanyRequirements/CompanyRequirements';
import FormManager from 'views/CandidateInfo/FormManagerView/FormManager';
import Companies from 'views/Companies/Companies';
import CompanyView from 'views/Company/CompanyView/CompanyView';
import CreateCompany from 'views/Company/CreateCompany/CreateCompany';
import CreateJobRole from 'views/CreateJobRole';
import EditJobRole from 'views/EditJobRole';
import CreateProjectView from 'views/Project/ProjectCreate';
import EditProjectView from 'views/Project/ProjectEdit';
import Projects from 'views/Projects/Projects';
import Report from 'views/Report/Report';
import ReportList from 'views/Reports/ReportList/ReportList';
import Reports from 'views/Reports/Reports';
import DocPage from 'views/Docs/DocPage';
import Docs from 'views/Docs/Docs';
import { Coderbyte } from 'views/Integrations/Coderbyte/Coderbyte';
import { Kombo } from 'views/Integrations/Kombo/Kombo';
import Integrations from 'views/Integrations/Integrations';
import CandidateUsage from 'views/Results/CandidateUsage/CandidateUsage';
import QBRData from 'views/Results/QBR/QBR';
import Results from 'views/Results/Results';
import SkillsValidated from 'views/Results/SkillsValidated/SkillsValidated';
import StageCSV from 'views/Results/StageCSV/StageCSV';
import Users from 'views/Results/Users/Users';
import BehaviourContent from 'views/StaticContent/BehaviourContent/BehaviourContent';
import InterviewQuestionContent from 'views/StaticContent/InterviewQuestionContent/InterviewQuestionContent';
import SoftSkills from 'views/StaticContent/SoftSkills/SoftSkills';
import StaticContent from 'views/StaticContent/StaticContent';
import TechnicalSkills from 'views/StaticContent/TechnicalSkills/TechnicalSkills';
import TraitContent from 'views/StaticContent/TraitContent/TraitContent';
import Traits from 'views/StaticContent/Traits/Traits';
import User from 'views/User/User';
import UserDetails from 'views/User/UserDetails/UserDetails';
import UserMain from 'views/User/UserMain/UserMain';
import Widget from 'views/Widget/Widget';
import Widgets from 'views/Widgets/Widgets';
import InterviewGuideBlueprint from '../views/InterviewGuideBlueprint/InterviewGuideBlueprint';

const RootRedirection = () => <Navigate to={appRoutes.projects.path} />;

const RootSwitch: FC = () => {
  return (
    <Routes>
      <Route path="/" element={<RootRedirection />} />

      {/* Projects routes */}
      <Route path={`${appRoutes.projects.path}`} element={<Projects />} />
      <Route
        path={projectsRoutes.createProject.path}
        element={<CreateProjectView />}
      />
      <Route
        path={projectsRoutes.editProjectView.path}
        element={<EditProjectView />}
      />
      {/* Results routes */}
      <Route element={<Results />}>
        <Route
          path={resultsRoutes.candidateResults.path}
          element={<StageCSV />}
        />
        <Route
          path={resultsRoutes.candidateUsage.path}
          element={<CandidateUsage />}
        />
        <Route
          path={resultsRoutes.skillsValidated.path}
          element={<SkillsValidated />}
        />
        <Route path={resultsRoutes.users.path} element={<Users />} />
        <Route path={resultsRoutes.QBRData.path} element={<QBRData />} />
        <Route
          path={appRoutes.results.path}
          element={<Navigate to={resultsRoutes.candidateResults.path} />}
        />
      </Route>

      {/* Company routes */}
      <Route path={appRoutes.companies.path} element={<Companies />} />

      <Route element={<Docs />}>
        <Route path={docsRoutes.page.path} element={<DocPage />} />
        <Route
          path={appRoutes.docs.path}
          element={<Navigate to={docsRoutes.page.url('coderbyte')} />}
        />
      </Route>

      <Route element={<Integrations />}>
        <Route
          path={integrationsRoutes.coderbyte.path}
          element={<Coderbyte />}
        />
        <Route path={integrationsRoutes.kombo.path} element={<Kombo />} />
        {/* Coderbyte stays the section's default landing page. */}
        <Route
          path={appRoutes.integrations.path}
          element={<Navigate to={integrationsRoutes.coderbyte.path} />}
        />
      </Route>

      <Route
        path={companyRoutes.createCompany.path}
        element={<CreateCompany />}
      />
      <Route path={companyRoutes.editCompany.path} element={<CompanyView />} />

      {/* Form manager routes */}
      <Route path={appRoutes.formManager.path} element={<CandidateInfo />}>
        <Route
          path={appRoutes.formManager.path}
          element={<Navigate to={formManagerRoutes.fields.path} />}
        />
        <Route
          path={formManagerRoutes.fields.path}
          element={<CandidateInfoFields />}
        />
        <Route
          path={formManagerRoutes.companyForms.path}
          element={<CandidateRequirements />}
        />
        <Route
          path={formManagerRoutes.downloadSubmittedForms.path}
          element={<FormManager />}
        />
      </Route>

      {/** Static content routes */}
      <Route element={<StaticContent />}>
        <Route
          path={appRoutes.content.path}
          element={<Navigate to={contentRoutes.softSkills.path} />}
        />
        <Route path={contentRoutes.softSkills.path} element={<SoftSkills />} />
        <Route path={contentRoutes.traits.path} element={<Traits />} />
        <Route
          path={contentRoutes.traitContent.path}
          element={<TraitContent />}
        />
        <Route
          path={contentRoutes.interviewQuestionContent.path}
          element={<InterviewQuestionContent />}
        />
        <Route
          path={contentRoutes.technicalSkills.path}
          element={<TechnicalSkills />}
        />
        <Route
          path={contentRoutes.behaviourContent.path}
          element={<BehaviourContent />}
        />
      </Route>

      {/** Reports routes */}
      <Route path={appRoutes.reports.path} element={<Reports />}>
        <Route
          path={appRoutes.reports.path}
          element={<Navigate to={reportsRoutes.reports.path} />}
        />
        <Route path={reportsRoutes.report.path} element={<Report />} />
        <Route path={reportsRoutes.reports.path} element={<ReportList />} />
        <Route path={reportsRoutes.widget.path} element={<Widget />} />
        <Route path={reportsRoutes.widgets.path} element={<Widgets />} />
      </Route>

      <Route path={appRoutes.users.path} element={<User />}>
        <Route path={appRoutes.users.path} element={<UserMain />} />
        <Route path={usersRoutes.user.path} element={<UserDetails />} />
      </Route>

      {/** Interview Guide Blueprint (hidden admin page) */}
      <Route
        path={interviewGuideBlueprintRoutes.blueprint.path}
        element={<InterviewGuideBlueprint />}
      />

      {/** Job Role routes */}
      <Route
        path={jobRolesRoutes.createJobRole.path}
        element={<CreateJobRole />}
      />
      <Route path={jobRolesRoutes.editJobRole.path} element={<EditJobRole />} />

      <Route path="*" element={<RootRedirection />} />
    </Routes>
  );
};

export default RootSwitch;
